// Shared contract between the game client (game/) and the backend (server/).
// The backend treats `gameData` as an opaque encrypted blob; only the game interprets it.

export const BOARD_SIZE = 10;
export const TRAY_SLOTS = 3;
export const ROTATE_REQUIREMENT = 100; // cleared cubes per earned rotation point (Unity: newRotateRequirement)
export const START_ROTATES = 1; // rotation points granted on a new game (Unity: rotates: 1)

/** A piece sitting in the drawer/tray, or already placed (null in slot). */
export interface SavedTrayPiece {
  shapeIndex: number; // index into the SHAPES table
  rotation: number; // 0 | 90 | 180 | 270
  value: number; // per-cube point value (floor(cellCount/2), min 1)
  rotationEnabled: boolean; // whether a rotation point has been spent on it
}

/** Full game state, persisted locally (localStorage) and, when logged in, synced to the server.
 *  Mirrors the role of the Unity `DataHolder`. */
export interface SaveState {
  version: 1;
  board: number[]; // length 100, 0 = empty, >0 = cube value, row-major (z*10 + x)
  tray: (SavedTrayPiece | null)[]; // length 3
  score: number;
  highScore: number;
  workingOnHighScore: boolean;
  rotates: number; // available rotation points
  rotateCharge: number; // progress toward the next rotation point (0..ROTATE_REQUIREMENT)
  gameInProgress: boolean;
  gameOver: boolean;
}

/** Player-facing settings, persisted separately from the run. */
export interface Settings {
  musicLevel: number; // 0..1
  soundLevel: number; // 0..1
  darkMode: boolean;
  useAmbient: boolean;
  /** Per-channel ambient mixer, 0..1 — order [fire, water, birds, chimes, chirps]
   *  (Unity AudioBlend.audioSources; default = DataHolder.audioBlend {50,15,50,25,40}/100). */
  ambientMix: number[];
  playerName: string;
  shownHowToPlay: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  musicLevel: 0.8,
  soundLevel: 0.8,
  darkMode: false,
  useAmbient: true,
  ambientMix: [0.5, 0.15, 0.5, 0.25, 0.4],
  playerName: '',
  shownHowToPlay: false,
};

// ---- Backend API contract ----

export interface ApiUser {
  username: string;
  email?: string;
}

export interface SessionResponse {
  authenticated: boolean;
  username?: string;
}

export interface UserDataResponse {
  success: boolean;
  message?: string;
  gameData: string; // decrypted JSON string of SaveState (or "" if none)
  highscore: number;
}

export interface SaveDataRequest {
  gameData: string; // JSON string of SaveState
  highScore: number;
}

export interface HighscoreEntry {
  rank: number;
  username: string;
  highscore: number;
}

export interface ApiResult {
  success: boolean;
  message?: string;
}
